import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Outlet, useMatch } from "react-router";

import { Avatar } from "@citrine/atoms/avatar";
import { ErrorBoundary, ErrorMessage } from "@citrine/atoms/error-boundary";
import { Spinner } from "@citrine/atoms/loading";
import {
  isDevelopment,
  isIntegrationTest,
  isProduction,
} from "@citrine/configuration";
import Integrations from "@citrine/integrations";
import { captureException } from "@citrine/integrations/sentry";
import type { IConfiguration } from "@citrine/resources/configuration";
import {
  ConfigurationClient,
  useConfiguration,
} from "@citrine/resources/configuration";
import { ProjectClient } from "@citrine/resources/project";
import type { IUser, Terms } from "@citrine/resources/user";
import { UserClient } from "@citrine/resources/user";
import { UserRequestClient } from "@citrine/resources/user-requests";

import { BannerAlerts } from "#src/components/banner-alert";
import {
  Catalyst,
  SetCatalystAgentAvailability,
  SetCatalystModelExpertAvailability,
} from "#src/components/catalyst";
import { DevTools } from "#src/components/dev-tools";
import type { NavRoute } from "#src/components/navbar";
import { Navbar, NavbarShell } from "#src/components/navbar";
import { QuerySuspense } from "#src/components/query-suspense";
import { SplashScreen } from "#src/components/splash-screen";
import { TermsModal } from "#src/components/terms-modal";
import { DATA_MANAGER_PATH } from "#src/pages/constants";

import * as sx from "./route.sx";

import "#src/styles/reactflow";

const ENABLE_SPLASH_SCREEN = isProduction || isIntegrationTest;

export function Component() {
  // do not attempt subsequent fetches until access token is refreshed
  const { data: me } = useSuspenseQuery(
    UserClient.getInstance().getMeOptions(),
  );

  const runtimeQuery = ConfigurationClient.getInstance().useGetRuntimeConfig();
  const termsQuery = UserClient.getInstance().useGetTermsAcceptance(me.id);

  const [showSplashScreen, setShowSplashScreen] =
    useState(ENABLE_SPLASH_SCREEN);

  const onReady = useCallback(() => {
    // delay hiding Splash Screen so page content has a chance to render
    window.setTimeout(() => {
      setShowSplashScreen(false);
    }, 200);
  }, []);

  return (
    <>
      {/* render Splash Screen manually so we can delay hiding it */}
      <SplashScreen show={showSplashScreen} />
      <QuerySuspense
        loading={null}
        queries={[runtimeQuery, termsQuery]}
        render={([runtimeConfig, termsAgreed]) => (
          <Content
            me={me}
            runtimeConfig={runtimeConfig}
            termsAgreed={termsAgreed}
            onReady={onReady}
          />
        )}
      />
    </>
  );
}

function Content({
  me,
  runtimeConfig,
  termsAgreed,
  onReady,
}: {
  me: IUser;
  runtimeConfig: IConfiguration;
  termsAgreed: Terms;
  onReady?: () => void;
}) {
  const { mutate: acceptTerms } =
    UserClient.getInstance().usePostTermsAcceptance(me.id);

  // now that the data is successfully loaded, we can hide the splash screen
  useEffect(() => {
    onReady?.();
  }, [onReady]);

  return (
    <ErrorBoundary
      fallback={<ErrorMessage showStackTrace={isDevelopment} />}
      onError={isProduction ? captureException : undefined}
    >
      {termsAgreed ? <Layout /> : <NavbarShell />}
      <TermsModal open={!termsAgreed} accept={acceptTerms} />

      <Integrations runtimeConfig={runtimeConfig} me={me} />
    </ErrorBoundary>
  );
}

export function Layout() {
  // the app won't render until a user object is loaded
  const { is_admin, screen_name } = UserClient.getInstance().useGetMe().data!;

  const configuration = useConfiguration();
  const projectPathMatch = useMatch("/projects/:projectId/*");
  const hasProjectToolbar = Boolean(projectPathMatch);

  const projectId = projectPathMatch?.params.projectId ?? "";
  const projectQuery = ProjectClient.getInstance().useGet(projectId);

  const teamPathMatch = useMatch("/data-manager/teams/:teamId/*");
  const teamId = teamPathMatch?.params.teamId ?? projectQuery.data?.team.id;

  return (
    <>
      {configuration.enableInsightsCopilotDemo ||
      configuration.enableCatalystAgent ? (
        <>
          <Catalyst teamId={teamId} />
          {/** Needs to be mounted within the Router */}
          <SetCatalystModelExpertAvailability />
          <SetCatalystAgentAvailability />
        </>
      ) : undefined}
      <BannerAlerts />
      <RoutedNavbar
        {...configuration}
        isAdmin={is_admin}
        screenName={screen_name}
        teamId={teamId}
      />

      <sx.Page data-testid="page" hasProjectToolbar={hasProjectToolbar}>
        <Suspense fallback={<Spinner />}>
          <Outlet />
        </Suspense>
      </sx.Page>

      <DevTools />
    </>
  );
}

function RoutedNavbar({
  isAdmin,
  screenName,
  teamId,
}: {
  isAdmin?: boolean;
  screenName: string;
  teamId?: string;
}) {
  const { formatMessage } = useIntl();

  const hasOutstandingRequests =
    UserRequestClient.getInstance().useHasOutstandingRequests();

  const routes = useMemo<NavRoute[]>(
    () =>
      [
        {
          "data-fullstoryid": "nav.data-manager" as const,
          path: teamId
            ? `${DATA_MANAGER_PATH}/teams/${teamId}`
            : DATA_MANAGER_PATH,
          label: formatMessage({ id: "nav.data-manager" }),
        },
        {
          path: "/projects",
          label: formatMessage({ id: "nav.projects" }),
        },
        isAdmin
          ? {
              "data-fullstoryid": "nav.admin" as const,
              path: "/admin",
              label: formatMessage({ id: "nav.admin" }),
              notification: hasOutstandingRequests,
            }
          : undefined,
      ].filter(Boolean),
    [formatMessage, isAdmin, hasOutstandingRequests, teamId],
  );

  return (
    <Navbar routes={routes}>
      <sx.AccountLink to="/account">
        <Avatar name={screenName} />
      </sx.AccountLink>
    </Navbar>
  );
}
